import { ReactElement, useContext, useEffect, useState } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { IBasicEntity, ICategoryQMOS, IDelayQMOS } from "../../../../types";
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from "@mui/material";
import { DelayContext } from "../../../../context/delayContext";
import { useAccount, useMsal } from "@azure/msal-react";

interface IDelayConfig {
  areaGroup: ICategoryQMOS | null;
  area: ICategoryQMOS | null;
  equipment: ICategoryQMOS | null;
  observation: ICategoryQMOS | null;
  reason: ICategoryQMOS | null;
  actionCode: ICategoryQMOS | null;
  responsibility: IBasicEntity | null;
  comment: string;
}

interface IDelayDialog {
  open: boolean;
  handleClose: any;
  selectedDelay: IDelayQMOS;
  updateDelay: (delayHistoryID: number, areaID: number | null, equipmentID: number | null, observationID: number | null,
    reasonID: number | null, responsibilityID: number | null, actionCodeID: number | null, comment: string, editedBy: string) => void;
}


const EditDelayDialog = ({ open, handleClose, selectedDelay, updateDelay }: IDelayDialog): ReactElement => {
  const delayContext = useContext(DelayContext);
  const [ config, setConfig ] = useState<IDelayConfig>({
    areaGroup: null,
    area: null,
    equipment: null,
    observation: null,
    reason: null,
    actionCode: null,
    responsibility: null,
    comment: ""
  });

  const { accounts } = useMsal();

  const account = useAccount(accounts[0] || {});


  useEffect(() => {

    const areaGroup = selectedDelay.areaGroup
      ? (delayContext.categories as ICategoryQMOS[]).find(x => selectedDelay.areaGroup && x.id === selectedDelay.areaGroup.id) ?? null
      : null;
    const area = areaGroup ? areaGroup.children.find(x => x.id === selectedDelay.area?.id) ?? null : null;
    const equipment = area ? area.children.find(x => x.id === selectedDelay.equipment?.id) ?? null : null;
    const observation = equipment ? equipment.children.find(x => x.id === selectedDelay.observation?.id) ?? null : null;
    const reason = observation ? observation.children.find(x => x.id === selectedDelay.reason?.id) ?? null : null;
    const actionCode = reason ? reason.children.find(x => x.id === selectedDelay.actionCode?.id) ?? null : null;
    const responsibility = selectedDelay.responsibility && delayContext.responsibility
      ? delayContext.responsibility.find(x => selectedDelay.responsibility && x.id === selectedDelay.responsibility.id) ?? null
      : null;

    setConfig({
      areaGroup,
      area,
      equipment,
      observation,
      reason,
      actionCode,
      responsibility,
      comment: selectedDelay.comment
    });

  }, [selectedDelay, delayContext]);


  const onHandleAccept =() => {
    updateDelay(selectedDelay.delayID,
      config.area ? config.area.id : null,
      config.equipment ? config.equipment.id : null,
      config.observation ? config.observation.id : null,
      config.reason ? config.reason.id : null,
      config.responsibility ? config.responsibility.id : null,
      config.actionCode ? config.actionCode.id : null,
      config.comment,
      account ? account.name ?? "N/A" : "N/A");
  };


  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg">
      <DialogTitle id="form-dialog-title">Modify Delay</DialogTitle>
      <DialogContent style={{ paddingTop: "1em !important" }} dividers={true}>

        <div className="display-flex-row" >
          <FormControl style={{
            margin: 1,
            minWidth: 250,
            width: 250,
          }} >
            <InputLabel id="machine-label">Area Group</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={config.areaGroup ? config.areaGroup.id : 0}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent<number>) => {
                const node = (delayContext.categories as ICategoryQMOS[]).find(x => x.id === event.target.value) ?? null;

                setConfig({
                  ...config,
                  areaGroup: node,
                  area: null,
                  equipment: null,
                  observation: null,
                  reason: null,
                  actionCode: null
                });
              }}>
              {<MenuItem value={0}>NONE</MenuItem>}
              {delayContext.categories && (delayContext.categories as ICategoryQMOS[]).map(child => {
                return <MenuItem key={`edit-areaGroup-${child.id}`} value={child.id}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>

          <FormControl style={{
            margin: 1,
            minWidth: 250,
            width: 250,
          }} >
            <InputLabel id="machine-label">Area</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={config.area ? config.area.id : 0}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent<number>) => {
                const node = config.areaGroup ? config.areaGroup.children.find(x => x.id === event.target.value) ?? null : null;

                setConfig({
                  ...config,
                  area: node,
                  equipment: null,
                  observation: null,
                  reason: null,
                  actionCode: null
                });
              }}>
              {<MenuItem value={0}>NONE</MenuItem>}
              {config.areaGroup && config.areaGroup.children.map(child => {
                return <MenuItem key={`edit-area-${child.id}`} value={child.id}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>

          <FormControl style={{
            margin: 1,
            minWidth: 250,
            width: 250,
          }} >
            <InputLabel id="machine-label">Equipment</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={config.equipment ? config.equipment.id : 0}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent<number>) => {
                const node = config.area ? config.area.children.find(x => x.id === event.target.value) ?? null : null;

                setConfig({
                  ...config,
                  equipment: node,
                  observation: null,
                  reason: null,
                  actionCode: null
                });
              }}>
              {<MenuItem value={0}>NONE</MenuItem>}
              {config.area && config.area.children.map(child => {
                return <MenuItem key={`edit-equipment-${child.id}`} value={child.id}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>
        </div>

        <div className="display-flex-row">
          <FormControl style={{
            margin: 1,
            minWidth: 250,
            width: 250,
          }}>
            <InputLabel id="machine-label">Observation</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={config.observation ? config.observation.id : 0}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent<number>) => {
                const node = config.equipment ? config.equipment.children.find(x => x.id === event.target.value) ?? null : null;

                setConfig({
                  ...config,
                  observation: node,
                  reason: null,
                  actionCode: null
                });
              }}>
              {<MenuItem value={0}>NONE</MenuItem>}
              {config.equipment && config.equipment.children.map(child => {
                return <MenuItem key={`edit-observation-${child.id}`} value={child.id}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>

          <FormControl style={{
            margin: 1,
            minWidth: 250,
            width: 250,
          }}>
            <InputLabel id="machine-label">Reason</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={config.reason ? config.reason.id : 0}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent<number>) => {
                const node = config.observation ? config.observation.children.find(x => x.id === event.target.value) ?? null : null;

                setConfig({
                  ...config,
                  reason: node,
                  actionCode: null
                });
              }}>
              {<MenuItem value={0}>NONE</MenuItem>}
              {config.observation && config.observation.children.map(child => {
                return <MenuItem key={`edit-reason-${child.id}`} value={child.id}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>

          <FormControl style={{
            margin: 1,
            minWidth: 250,
            width: 250,
          }}>
            <InputLabel id="machine-label">Action Code</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={config.actionCode ? config.actionCode.id : 0}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent<number>) => {
                const node = config.reason ? config.reason.children.find(x => x.id === event.target.value) ?? null : null;
                setConfig({
                  ...config,
                  actionCode: node
                });
              }}>
              {<MenuItem value={0}>NONE</MenuItem>}
              {config.reason && config.reason.children.map(child => {
                return <MenuItem key={`edit-action-code-${child.id}`} value={child.id}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>
        </div>

        <div className="display-flex-row">
          <FormControl style={{
            margin: 1,
            minWidth: 250,
            width: 250,
          }} >
            <InputLabel id="machine-label">Responsibility</InputLabel>
            <Select
              variant="standard"
              labelId="machine-label"
              value={config.responsibility ? config.responsibility.id : 0}
              displayEmpty={true}
              onChange={(event: SelectChangeEvent<number>) => {
                const node = delayContext.responsibility ? delayContext.responsibility.find(x => x.id === event.target.value) ?? null : null;
                setConfig({
                  ...config,
                  responsibility: node
                });
              }}>
              {<MenuItem value={0}>NONE</MenuItem>}
              {delayContext.responsibility && delayContext.responsibility.map(child => {
                return <MenuItem key={`edit-responsibility-${child.id}`} value={child.id}>{child.name}</MenuItem>;
              })
              }
            </Select>
          </FormControl>
        </div>

        <div style={{ margin: 8 }}>
          <TextField
            variant="standard"
            label="Comments"
            multiline={true}
            minRows={4}
            maxRows={4}
            value = {config.comment}
            fullWidth={true}
            onChange={event => setConfig({ ...config, comment: event.target.value })}/>
        </div>

      </DialogContent>
      <DialogActions>
        <Button onClick={onHandleAccept} color="primary" variant="contained" disabled={ !config.areaGroup || !config.areaGroup || !config.responsibility || !config.equipment || config.comment.trim().length === 0 } >
          Accept
        </Button>
        <Button onClick={handleClose} color="secondary" variant="contained" >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditDelayDialog;
